/**
 * The persisted data model.
 *
 * Field names and value types are deliberately identical to the original
 * single-file app, so data already saved on the tablet under `jdc_anso_v2`
 * loads without conversion, and JSON backups stay interchangeable in both
 * directions. That is why `score`, `max` and `period` are strings: they come
 * straight from `<input>.value` and were persisted that way.
 */

import type { DayIndex } from '../lib/calendar.ts';

/** Sync envelope carried by every row. */
export interface SyncMeta {
  /** ISO timestamp, assigned by Postgres once the row has been pushed. */
  updatedAt: string;
  /** Soft delete. A hard delete could never propagate to the other device. */
  deletedAt: string | null;
  /** Local edit not yet accepted by the server. */
  dirty: boolean;
}

export interface Entity extends SyncMeta {
  id: string;
}

export interface Settings extends SyncMeta {
  teacher: string;
  className: string;
  schoolYear: string;
}

export interface Student extends Entity {
  /** Display name; kept as the single source of truth for older records. */
  name: string;
  firstName: string;
  lastName: string;
  /** ISO date (YYYY-MM-DD) or empty. */
  birth: string;
  /** Philosophy/religion option. GDPR Article 9 special-category data. */
  philo: string;
}

export interface Task extends Entity {
  text: string;
  done: boolean;
  /** ISO date for calendar entries; older imports may still contain free text. */
  due: string;
}

export interface Postit extends Entity {
  text: string;
  color: string;
}

/** Slot -1 is the rituals band; 0..6 are the teaching periods. */
export type SlotIndex = -1 | 0 | 1 | 2 | 3 | 4 | 5 | 6;

export interface Lesson extends Entity {
  /** ISO Monday of the week this lesson belongs to. */
  weekStart: string;
  day: DayIndex;
  slot: SlotIndex;
  subject: string;
  domain: string;
  /** Derived from `subject`; persisted so old records keep rendering. */
  color: string;
  /** Teacher-facing number used to find the paper lesson in a binder. */
  lessonNumber: string;
  title: string;
  detail: string;
  /**
   * Ids of the tronc commun attendus this lesson works on.
   * See `src/lib/referentiel.ts`; `competence` is derived from these.
   */
  competenceIds: string[];
  /**
   * The chosen attendus as plain text, one per line.
   *
   * Derived, never typed: it keeps the JSON backup readable by the original
   * app, and keeps lessons saved before the picker existed rendering.
   */
  competence: string;
  material: string;
  differentiation: string;
}

/**
 * A lesson detached from any particular week, ready to be stamped onto one.
 *
 * Synced like everything else: a "semaine type" saved on the PC has to reach
 * the tablet, otherwise the feature is useless on the device it matters on.
 */
export interface WeekTemplateEntry extends Entity {
  day: DayIndex;
  slot: SlotIndex;
  subject: string;
  domain: string;
  color: string;
  lessonNumber: string;
  title: string;
  detail: string;
  competenceIds: string[];
  competence: string;
  material: string;
  differentiation: string;
}

export interface Observation extends Entity {
  studentId: string;
  /** ISO date (YYYY-MM-DD). */
  date: string;
  /**
   * The lesson that was running when the note was taken, or '' when there was
   * none. Never a foreign key, for the same reason as `Evaluation.lessonId`:
   * deleting a lesson must not take the observations made during it.
   */
  lessonId: string;
  /**
   * The copies made of that lesson by duplicating it, which the note belongs
   * to as well. See `src/lib/lesson-links.ts`.
   */
  extraLessonIds: string[];
  subject: string;
  difficulty: string;
  note: string;
  support: string;
  /**
   * Whether a stylus note exists. The PNG itself lives in IndexedDB locally
   * and in Supabase Storage remotely — never inline, which is what used to
   * blow through the 5 MB localStorage quota.
   */
  hasHandwriting: boolean;
  /** Storage object path, set once the image has been pushed. */
  handwritingPath: string | null;
}

export interface Evaluation extends Entity {
  studentId: string;
  subject: string;
  /**
   * Report domain, from `BULLETIN_DOMAINS` — empty for a subject that has
   * none. Deliberately not the lesson's domain, which is finer grained; see
   * `src/lib/bulletin-subjects.ts`.
   */
  domain: string;
  /** The lesson this evaluates, or '' for an evaluation typed from scratch. */
  lessonId: string;
  /**
   * Copies of that lesson the mark is attached to as well. The report groups
   * by `lessonId` alone, so a duplicated lesson never counts a mark twice.
   */
  extraLessonIds: string[];
  title: string;
  /**
   * Kept as a string: it comes from an input and may be empty. "A" means the
   * pupil was absent, which is not the same as zero — it drops out of the
   * average entirely rather than counting against them.
   */
  score: string;
  max: string;
  /** Report period: "1", "2" or "3". */
  period: string;
  /** ISO date; absent on individual evaluations in older data. */
  date: string;
}

export interface Meeting extends Entity {
  /** ISO date (YYYY-MM-DD). */
  date: string;
  title: string;
  notes: string;
}

export type SkillStatus = 'À travailler' | 'En cours' | 'Acquis';

export interface Skill extends Entity {
  subject: string;
  status: SkillStatus;
  title: string;
  /** Optional: a skill may target the whole class. */
  studentId: string;
}

/** The collections that sync, one table each in Postgres. */
export interface Collections {
  students: Student[];
  tasks: Task[];
  postits: Postit[];
  lessons: Lesson[];
  observations: Observation[];
  evaluations: Evaluation[];
  meetings: Meeting[];
  skills: Skill[];
  weekTemplate: WeekTemplateEntry[];
}

export type CollectionName = keyof Collections;

export const COLLECTION_NAMES = [
  'students',
  'tasks',
  'postits',
  'lessons',
  'observations',
  'evaluations',
  'meetings',
  'skills',
  'weekTemplate',
] as const satisfies readonly CollectionName[];

/** Postgres table name for each collection. */
export const TABLE_BY_COLLECTION: Readonly<Record<CollectionName, string>> = {
  students: 'students',
  tasks: 'tasks',
  postits: 'postits',
  lessons: 'lessons',
  observations: 'observations',
  evaluations: 'evaluations',
  meetings: 'meetings',
  skills: 'skills',
  weekTemplate: 'week_template',
};

export interface AppData extends Collections {
  /** Singleton row rather than a collection. */
  settings: Settings;
}
